import { Badge, Button, DataList, Flex, Text, Tooltip } from "@radix-ui/themes";

import { useMemo } from "react";
import { Link } from "react-router-dom";
import { DashboardOrder } from "@/shared/api";
import { usePaginatedQuery } from "../hooks/usePaginatedQuery";
import { DataTable } from "@/pages/dashboard-order-details/ui/data-table";
import { Pagination } from "./pagination";
import { columnHelper, visibleColumnNames } from "../lib/orders-helpers";
import { DashboardFilters } from "../lib/types";
import {
  getStageStatusColor,
  STAGE_STATUS_TEXT,
} from "@/features/assignment/lib";
import { SortingState } from "@tanstack/react-table";
import { parseSorting } from "../lib/helpers";

enum StageName {
  journey_annotation = "Journey Annotation",
  transaction_annotation = "Transaction Annotation",
  track_persistence_annotation = "Track Persistence Annotation",
}

type DashboardOrdersProps = {
  filters: DashboardFilters;
  sorting: SortingState;
  onFiltersChange: (filters: DashboardFilters) => void;
  onSortingChange: (sorting: SortingState) => void;
};

export function DashboardOrders({
  filters,
  sorting,
  onSortingChange,
}: DashboardOrdersProps) {
  const {
    data,
    currentPage,
    setCurrentPage,
    queryResult,
    totalPages,
    pageSize,
    setPageSize,
    totalCount,
  } = usePaginatedQuery<DashboardOrder>(
    {
      ...filters,
      ordering: parseSorting(sorting),
    },
    {}
  );

  const columns = useMemo(() => {
    return [
      columnHelper.display({
        id: "index",
        header: "#",
        minSize: 50,
        size: 50,
        enableSorting: false,
        cell: ({ row }) => (
          <Flex justify="between">
            {currentPage * pageSize + row.index + 1}
          </Flex>
        ),
      }),
      columnHelper.accessor("order.id", {
        header: visibleColumnNames["order_id"],
        minSize: 200,
        size: 200,
        enableSorting: false,
        cell: ({ cell }) => (
          <Flex justify="between">
            <Text className="line-clamp-1 break-all">{cell.getValue()}</Text>
            <Button asChild variant="soft" color="gray" size="1">
              <Link
                to={`orders/${cell.getValue()}?requestId=${cell.row.original.request_id}`}
              >
                Open
              </Link>
            </Button>
          </Flex>
        ),
      }),
      columnHelper.accessor("request_id", {
        id: "request_id",
        header: visibleColumnNames["request_id"],
        size: 300,
      }),
      columnHelper.display({
        id: "stage-statuses",
        minSize: 320,
        size: 320,
        enableSorting: false,
        header: visibleColumnNames["stage_statuses"],
        cell: ({ row }) => (
          <DataList.Root className="gap-y-1">
            {row.original.order.stage_detail_statuses.map((status) => (
              <DataList.Item key={status.stage}>
                <DataList.Label highContrast>
                  {StageName[status.stage]}
                </DataList.Label>
                <DataList.Value>
                  <Badge color={getStageStatusColor(status.status)}>
                    {STAGE_STATUS_TEXT[status.status]}
                  </Badge>
                </DataList.Value>
              </DataList.Item>
            ))}
          </DataList.Root>
        ),
      }),
      columnHelper.accessor("order.order_source", {
        enableSorting: false,
        header: visibleColumnNames["order_source"],
      }),
      columnHelper.accessor("order.time", {
        id: "requested_at",
        header: visibleColumnNames["time"],
        size: 250,
      }),
      columnHelper.accessor("pipeline_version", {
        id: "pipeline_version",
        header: visibleColumnNames["pipeline_version"],
        size: 250,
      }),
      columnHelper.accessor("pipeline_description", {
        id: "pipeline_description",
        header: visibleColumnNames["pipeline_description"],
        size: 250,
        cell: ({ row }) =>
          !row.original?.pipeline_description ? (
            "-"
          ) : (
            <Tooltip content={row.original?.pipeline_description}>
              <Text className="line-clamp-3">
                {row.original?.pipeline_description}
              </Text>
            </Tooltip>
          ),
      }),
      columnHelper.accessor("order.created_at", {
        id: "created_at",
        header: visibleColumnNames["created_at"],
        size: 250,
      }),

      columnHelper.display({
        id: "z_created_at",
        enableSorting: false,
        header: visibleColumnNames["z_created_at"],
        size: 250,
        cell: ({ row }) =>
          row.original?.zone_based_prediction_evaluation?.created_at || "-",
      }),
      columnHelper.display({
        id: "z_f1_score",
        enableSorting: false,
        header: visibleColumnNames["z_f1_score"],
        cell: ({ row }) =>
          row.original?.zone_based_prediction_evaluation?.f1_score || "-",
      }),
      columnHelper.display({
        id: "z_true_positives_count",
        enableSorting: false,
        header: visibleColumnNames["z_true_positives_count"],
        cell: ({ row }) =>
          row.original?.zone_based_prediction_evaluation
            ?.true_positives_count || "-",
      }),
      columnHelper.display({
        id: "p_created_at",
        enableSorting: false,
        header: visibleColumnNames["p_created_at"],
        size: 250,
        cell: ({ row }) =>
          row.original?.pin_based_prediction_evaluation?.created_at || "-",
      }),
      columnHelper.display({
        id: "p_f1_score",
        enableSorting: false,
        header: visibleColumnNames["p_f1_score"],
        cell: ({ row }) =>
          row.original?.pin_based_prediction_evaluation?.f1_score || "-",
      }),
      columnHelper.display({
        id: "p_true_positives_count",
        enableSorting: false,
        header: visibleColumnNames["p_true_positives_count"],
        cell: ({ row }) =>
          row.original?.pin_based_prediction_evaluation?.true_positives_count ||
          "-",
      }),
      columnHelper.accessor("journey_count", {
        header: visibleColumnNames["journey_count"],
        enableSorting: false,
      }),
      columnHelper.accessor("elapsed_time", {
        header: visibleColumnNames["elapsed_time"],
      }),
    ];
  }, [currentPage, pageSize]);

  return (
    <Flex direction="column" className="h-[calc(100dvh-400px)]">
      <DataTable
        loading={queryResult.isFetching}
        isSuccess={queryResult.isSuccess}
        totalCount={totalCount}
        data={data}
        columns={columns}
        title="Dashboard Orders"
        sorting={sorting}
        onSortingChange={onSortingChange}
        emptyTitle="Empty"
        emptyDescription={""}
        renderPagination={
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        }
      />
    </Flex>
  );
}
